import React, { useMemo, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useUser } from "src/business-types/grocery/components/UserContext";
import clsx from "clsx";
import { ThemeBasedColorHeader, FontColor } from "src/shared/modules/profile/theme/themeCheacker";
import { reactAppVersion } from "src/features/auth/axiosConfig";

/* ---------------- ICON ---------------- */
const TabIcon = ({ label }: { label: string }) => {
  const iconMap: Record<string, string> = {
    "Print Settings": "🖨️",
    "POS Config": "🖥️",
    "Table & Billing": "🪑",
    "Terms & Conditions": "📜",
    "QR Codes": "📱",
    "Reports": "📊",
    "Business Operation": "🕒",
    "Food Types": "🍔",
    "Print Config": "🖨️",
    "Receipt Layouts": "🧾",
    "Payment Apps": "💳",
    "Credit Control": "💰",
  };

  return <span className='text-lg'>{iconMap[label] || "•"}</span>;
};

const basePath = `/grocery/${reactAppVersion}/setting/configuration/`;

interface TabItem {
  id: string;
  path: string;
  label: string;
}

const GroceryNavigationBar = () => {
  const { user, userConfigration } = useUser();
  const location = useLocation();

  /* ---------------- TABS ---------------- */
  const tabs: TabItem[] = useMemo(
    () => [
      { id: "table", path: `${basePath}table-billing`, label: "Table & Billing" },
      { id: "pos-config", path: `${basePath}pos-config`, label: "POS Config" },
      // { id: "print-layouts", path: `${basePath}print-layouts`, label: "Receipt Layouts" },
      { id: "reports", path: `${basePath}reports`, label: "Reports" },
      ...(user?.businessType === "Restaurant"
        ? [
          { id: "business", path: `${basePath}business-operation`, label: "Business Operation" },
          { id: "food", path: `${basePath}food-types`, label: "Food Types" },
        ]
        : []),
      { id: "qr", path: `${basePath}qr-codes`, label: "QR Codes" },
      { id: "print", path: `${basePath}print-settings`, label: "Print Settings" },
      { id: "terms", path: `${basePath}terms-conditions`, label: "Terms & Conditions" },
      { id: "print-config", path: `${basePath}print-config`, label: "Print Config" },
      ...(userConfigration?.creditControlEnabled
        ? [{
          id: "credit-control",
          path: `${basePath}credit-control`,
          label: "Credit Control",
        }]
        : []),
      // { id: "payment-apps", path: `${basePath}payment-apps`, label: "Payment Apps" },
    ],
    [user?.businessType, userConfigration?.creditControlEnabled],
  );

  /* ---------------- RENDER ITEM ---------------- */
  const renderItem = useCallback(
    (tab: TabItem) => {
      const isActive = location.pathname.startsWith(tab.path);

      return (
        <NavLink
          key={tab.id}
          to={tab.path}
          className={clsx("flex items-center w-full whitespace-nowrap font-bold py-2 px-2 my-2 rounded-md transition-all duration-200 shadow-sm", {
            "border border-gray-200 shadow-lg": isActive,
            [ThemeBasedColorHeader()]: isActive,
            [FontColor()]: !isActive,
            "hover:-translate-y-1 hover:shadow-md": !isActive,
          })}
        >
          <TabIcon label={tab.label} />
          <span className='ml-2 truncate'>{tab.label.length > 10 ? tab.label.slice(0, 10) + "..." : tab.label}</span>
        </NavLink>
      );
    },
    [location.pathname],
  );

  /* ---------------- UI ---------------- */
  return (
    <div className='h-full'>
      {/* SIDEBAR (Same as Menu design) */}
      <div className={clsx("w-full h-full flex flex-col items-center pt-4 rounded-2xl bg-white")}>
        <div className='w-full px-2'>{tabs.map(renderItem)}</div>
      </div>
    </div>
  );
};

export default React.memo(GroceryNavigationBar);
